import type { ApiConfig } from '@de/types/config';
import type { InterpolatedRequest } from '../data-sources/fetchDataSource';
import type { DataSourceDeclaration } from '@de/providers/DataSourceManager.types';
import {
  normalizeOfferingsMatchItem,
  type OfferingMatchItem,
} from '../mappers/normalizeOfferingsMatchItem';
import {
  OFFERINGS_MATCH_PATH,
  OFFERINGS_SEARCH_PATH,
} from './offeringsResultsSourceIdentity';

export {
  isStudyMatchResultsSource,
  OFFERINGS_SEARCH_PATH,
  UNQUALIFIED_FORMAT_SUBTYPE,
} from './offeringsResultsSourceIdentity';

/**
 * Bucket keys the Offerings `/match` response groups results under. Each
 * bucket is `{ data: SearchOfferingResultDto[], pagination }` (the DSL
 * repeaters iterate `<bucket>.data`); older shapes expose the bucket as a
 * bare array. The coercer normalizes either shape.
 */
const OFFERINGS_MATCH_BUCKETS = [
  'exact',
  'recommended',
  'related',
  'spotlight',
] as const;

/**
 * Build a post-fetch response transform that normalizes Offerings
 * search items into the flat `Result` card contract. It covers two
 * routes that share the same `SearchOfferingResultDto` item shape:
 *   - `POST /v1/offerings/search/match` — bucketed
 *     (`{ exact, recommended, related, spotlight }`).
 *   - `POST /v1/offerings/search` — flat (`{ data, pagination, tracking }`),
 *     used by single-list (StudyMatch-style) result pages.
 *
 * Why this is a bridge-built, service-shaped transform (mirrors
 * {@link buildMobiusBodyCoercer}):
 *   - Both payloads are UUID-native and nest school data under
 *     `organizations[]`, while the `Result` contract the cards, governed
 *     lead flow, dedupe registry, and offering detail-by-id drawer all
 *     consume expects flat, legacy ids. The reshape is service knowledge,
 *     not a general DSM concern, so it lives in the bridge rather than in
 *     `fetchDataSource`.
 *   - It runs on the raw response (before `responsePath` extraction) so a
 *     single transform normalizes every bucket / the flat `data` array
 *     regardless of which path a given binding/repeater reads.
 *
 * This is an interim seam; the durable fix is service-/BFF-side
 * normalization — retire this when that lands.
 *
 * Returns `undefined` when no Offerings origin is configured, so callers
 * can pass the result straight to DSM without a null branch. The returned
 * transform is pure: when the request isn't a POST to one of the two
 * normalized routes (or its origin isn't an Offerings origin), it returns
 * the response unchanged.
 */
export function buildOfferingsMatchResponseCoercer(
  apiConfig: ApiConfig,
):
  | ((
      res: unknown,
      req: InterpolatedRequest,
      decl?: DataSourceDeclaration,
    ) => unknown)
  | undefined {
  const offeringsOrigins = new Set(
    [apiConfig.offeringsServiceBaseUrl, apiConfig.offeringsSearchApiUrl]
      .map(extractOrigin)
      .filter((origin): origin is string => origin !== null),
  );
  if (offeringsOrigins.size === 0) return undefined;

  return (res, req, decl) => {
    if (req.method !== 'POST') return res;
    const path = pathnameOf(req.endpoint);
    if (path !== OFFERINGS_MATCH_PATH && path !== OFFERINGS_SEARCH_PATH) {
      return res;
    }
    // Absolute endpoints (the production shape after declaration
    // resolution) must target an Offerings origin; path-only endpoints
    // (tests / standalone hosts) carry no origin and are gated on the
    // path alone.
    const reqOrigin = extractOrigin(req.endpoint);
    if (reqOrigin && !offeringsOrigins.has(reqOrigin)) return res;
    // Pass the endpoint path so each item is stamped with `resultsSource`.
    // The flat-search related-split is opt-in per source (`bucketRelated`):
    // only steps that render `related.data` in a separate repeater want it.
    // Single-list viewMore steps leave it off so every row stays in `data[]`.
    return path === OFFERINGS_MATCH_PATH
      ? normalizeOfferingsMatchResponse(res, path)
      : normalizeOfferingsSearchResponse(
          res,
          path,
          decl?.bucketRelated === true,
        );
  };
}

/**
 * Normalize the flat `/v1/offerings/search` envelope. The top-level `data`
 * array is mapped through {@link normalizeOfferingsMatchItem};
 * `pagination`, `tracking`, and any other siblings pass through untouched.
 * Returns the response unchanged when `data` is absent or not an array, so
 * a malformed/empty payload is a no-op rather than an error.
 *
 * Related-item bucketing (offerings `includeRelated: true`): the flat search
 * response tags related offerings with `related: true` on the row rather than
 * splitting them into a separate array. The CE runtime, however, buckets by
 * array **location** — `platform.dataRepeater` iterates a dot-path
 * (`data` vs `related.data`), not a per-row flag.
 *
 * That split is **opt-in per source** via `bucketRelated` (EDUN-14551): only
 * steps that render `related.data` in a separate repeater want it. When
 * `bucketRelated` is true we partition here at the response boundary, mirroring
 * the `/match` bucket shape — items flagged `related: true` move to a
 * `related: { data: [...] }` sibling and top-level `data` keeps the primary
 * (non-related) items (the `related` bucket is only added when related items
 * are present). When false (default), every row is normalized but stays flat in
 * `data[]` with its `related` flag intact, so a single-list viewMore repeater
 * counts all fetched rows. A plain search response (no `includeRelated`) is
 * unchanged either way.
 */
function normalizeOfferingsSearchResponse(
  res: unknown,
  path: string,
  bucketRelated: boolean,
): unknown {
  if (!isPlainObject(res)) return res;
  if (!Array.isArray(res.data)) return res;
  // One `sortingReferenceId` per response, shared by every card. `''` when absent
  // (→ omitted by the normalizer) rather than a uuid that correlates to nothing.
  const listId = extractSortingReferenceId(res) ?? '';
  const normalize = (item: unknown) =>
    normalizeOfferingsMatchItem(item as OfferingMatchItem, path, listId);

  // Inline (default): normalize every row, leave the list flat.
  if (!bucketRelated) {
    return { ...res, data: res.data.map(normalize) };
  }

  const primary: unknown[] = [];
  const related: unknown[] = [];
  for (const item of res.data) {
    if (isRelatedItem(item)) related.push(item);
    else primary.push(item);
  }

  const next: Record<string, unknown> = {
    ...res,
    data: primary.map(normalize),
  };
  if (related.length > 0) {
    next.related = { data: related.map(normalize) };
  }
  return next;
}

// A flat-search row is a related offering when the service tags it with
// `related: true` (offerings `includeRelated: true`). Malformed rows
// (`null`, primitives) are treated as non-related so they stay in the
// primary list and the normalizer's own totality handles them.
function isRelatedItem(item: unknown): boolean {
  return isPlainObject(item) && item.related === true;
}

function normalizeOfferingsMatchResponse(res: unknown, path: string): unknown {
  if (!isPlainObject(res)) return res;
  // `sortingReferenceId` may live at the root or under `data`; prefer root, then
  // inner, else ''. (Separate `/search` requests each carry their own id.)
  // Buckets sit at the root or under a `data` envelope, depending on the
  // service response shape; normalize wherever the bucket keys live.
  if (hasAnyBucket(res)) {
    const listId = extractSortingReferenceId(res) ?? '';
    return normalizeBucketContainer(res, path, listId);
  }
  if (isPlainObject(res.data) && hasAnyBucket(res.data)) {
    const listId =
      extractSortingReferenceId(res) ??
      extractSortingReferenceId(res.data) ??
      '';
    const nextData = normalizeBucketContainer(res.data, path, listId);
    return nextData === res.data ? res : { ...res, data: nextData };
  }
  return res;
}

// Read the offerings `tracking.sortingReferenceId` off a response container,
// or `undefined` when absent/empty.
function extractSortingReferenceId(
  container: Record<string, unknown>,
): string | undefined {
  const tracking = container.tracking;
  if (!isPlainObject(tracking)) return undefined;
  const ref = tracking.sortingReferenceId;
  return typeof ref === 'string' && ref.length > 0 ? ref : undefined;
}

function hasAnyBucket(obj: Record<string, unknown>): boolean {
  return OFFERINGS_MATCH_BUCKETS.some((bucket) => bucket in obj);
}

function normalizeBucketContainer(
  container: Record<string, unknown>,
  path: string,
  listId: string,
): Record<string, unknown> {
  let next: Record<string, unknown> | null = null;
  for (const bucket of OFFERINGS_MATCH_BUCKETS) {
    if (!(bucket in container)) continue;
    const normalized = normalizeBucket(container[bucket], path, listId);
    if (normalized === container[bucket]) continue;
    next ??= { ...container };
    next[bucket] = normalized;
  }
  return next ?? container;
}

// Only two bucket value shapes are normalized: a bare item array, or a
// `{ data: [] }` envelope. Any other shape passes through untouched — items
// aren't dropped (bindings simply won't resolve) and nothing throws.
function normalizeBucket(
  bucket: unknown,
  path: string,
  listId: string,
): unknown {
  if (Array.isArray(bucket)) {
    return bucket.map((item) =>
      normalizeOfferingsMatchItem(item as OfferingMatchItem, path, listId),
    );
  }
  if (isPlainObject(bucket) && Array.isArray(bucket.data)) {
    return {
      ...bucket,
      data: bucket.data.map((item) =>
        normalizeOfferingsMatchItem(item as OfferingMatchItem, path, listId),
      ),
    };
  }
  return bucket;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function pathnameOf(endpoint: string): string {
  if (endpoint.startsWith('/')) {
    return endpoint.split(/[?#]/)[0] ?? '';
  }
  try {
    return new URL(endpoint).pathname;
  } catch {
    return '';
  }
}

function extractOrigin(value: string | undefined): string | null {
  if (!value) return null;
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }
    return parsed.origin.toLowerCase();
  } catch {
    return null;
  }
}
